import DataConsentModal from '@components/utils/data-consent-modal';
import { checkExistingConsent } from '@components/utils/data-consent-modal/consentStorage';
import { DATA_CONSENT_POLICY_VERSION } from '@components/utils/data-consent-modal/policy';
import { showToast } from '@components/utils/helper';
import PhoneVerificationModal, { checkExistingVerification } from '@components/utils/phone-verification-modal';
import ReadinessChecklist from '@components/utils/readiness-checklist';
import SoftBlockModal from '@components/utils/soft-block-modal';
import StepIndicator from '@components/utils/step-indicator';
import { pageurl } from '@constants/pageurl';
import { yupResolver } from '@hookform/resolvers/yup';
import { type ICreateObserverApplicationRequest } from '@interface/IObserver';
import { useCreateObserverApplicationMutation } from '@store/api/observerApi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useReactToPrint } from 'react-to-print';

import { type KYCReviewReason } from '../../../../interface/IAgents';
import { agentType, locationType } from '../../../../store/types';
import { type ITabheaderObject } from '../../../utils/hooks';
import { BodyContainer as BCColumn } from '../../../utils/reusable/styled';
import { buildSuccessPayload, storeSuccessPayload } from '../../application-success/types';
import { trustedPayloadFields } from '../trustedInvite';
import BasicInfo from './basic-information';
import BrandedHero from './BrandedHero';
import FieldReadiness from './field-readiness';
import {
	_codeSeparator,
	type electionType,
	fieldReadinessSchema,
	type ICAChild,
	type IFieldReadinessSchema,
	type ILocationDetailsSchema,
	type IPersonalDetailsSchema,
	type ISummarySchema,
	locationDetailsSchema,
	makeFieldReadinessSchema,
	makePersonalDetailsSchema,
	summarySchema,
	type tabTitle,
} from './helper';
import LocationInfo from './location';
import PrintSummary from './print';
import StatusComponent from './status';
import { CreateAgentForm, PageEyebrow, PageHeaderBlock, PageSubtitle, PageTitle, ProfileUserSection } from './styled';
import FormSummary from './summary';
import TrustedInviteNotice from './TrustedInviteNotice';
import VideoThumbnailCard from './VideoThumbnailCard';

const STEP_TITLES: Record<tabTitle, { title: string; subtitle: string }> = {
	'Basic Information': {
		title: 'Verify your identity',
		subtitle: 'We’ll quickly confirm you’re a real, registered voter — takes about a minute.',
	},
	'Location Information': {
		title: 'Where will you serve?',
		subtitle: 'Pick the polling unit you’ll be assigned to on election day.',
	},
	'Field Readiness': {
		title: 'Field readiness',
		subtitle: 'A few quick questions about the kit you’ll bring on the day.',
	},
	'Summary': {
		title: 'Review your application',
		subtitle: 'Last look before we submit. You can still go back and edit anything.',
	},
};

const formatElectionEyebrow = (name?: string, dateIso?: string): string => {
	const parts: string[] = [];
	if (name) parts.push(name);
	if (dateIso) {
		try {
			const d = new Date(dateIso);
			parts.push(
				d.toLocaleDateString('en-GB', {
					day: 'numeric',
					month: 'short',
					year: 'numeric',
				})
			);
		} catch {
			// ignore — best-effort
		}
	}
	return parts.join(' · ').toUpperCase();
};

const CreateAgent = ({ states, actions, guid, customization, role = 'agent', partnerData, isTrustedInvite }: ICAChild) => {
	const navigate = useNavigate();
	const isObserver = role === 'observer';

	const contentRef = useRef<HTMLDivElement>(null);
	const reactToPrintFn = useReactToPrint({ contentRef });

	const { clearAction, createAgentAction } = actions!;

	const [isPhoneVerified, setIsPhoneVerified] = useState(false);
	const [verifiedPhone, setVerifiedPhone] = useState('');
	const [isDataConsentConfirmed, setIsDataConsentConfirmed] = useState(false);
	const [consentReference, setConsentReference] = useState<string | null>(null);
	const [isReadinessConfirmed, setIsReadinessConfirmed] = useState(false);

	// Soft-block modal: opened when KYC review is required and user attempts submit
	const [softBlockOpen, setSoftBlockOpen] = useState(false);
	const [softBlockReasons, setSoftBlockReasons] = useState<KYCReviewReason[]>([]);
	const submitConfirmedRef = useRef(false);

	const electionData = states?.agent?.electionguid?.data;
	const partyData = states?.agent?.partyguid?.data;

	const loadStates = states?.location?.getAllLocationStates_Loading;
	const dataStates = states?.location?.getAllLocationStates;
	const datalga = states?.location?.getAllLocationLGAsInState;
	const loadLga = states?.location?.getAllLocationLGAsInState_Loading;
	const dataWard = states?.location?.getAllLocationWardsInLGA;
	const loadWard = states?.location?.getAllLocationWardsInLGA_Loading;
	const dataPollingUnit = states?.location?.getAllLocationPollingUnitsInWard;
	const loadPollingUnit = states?.location?.getAllLocationPollingUnitsInWard_Loading;
	const loadAgent = states?.agent?.createAgentLoading;
	const dataAgent = states?.agent?.createAgent;

	const [createObserver] = useCreateObserverApplicationMutation();

	const [selectedState, setSelectedState] = useState<number>(0);

	const personalDetailsHookForm = useForm<IPersonalDetailsSchema>({
		resolver: yupResolver(makePersonalDetailsSchema(role)),
		mode: 'onChange',
		reValidateMode: 'onChange',
	});
	const locationDetailsHookForm = useForm<ILocationDetailsSchema>({
		resolver: yupResolver(locationDetailsSchema),
		mode: 'onChange',
		reValidateMode: 'onChange',
	});
	const fieldReadinessHookForm = useForm<IFieldReadinessSchema>({
		resolver: yupResolver(makeFieldReadinessSchema(role)),
		mode: 'onChange',
		reValidateMode: 'onChange',
	});
	const summaryHookForm = useForm<ISummarySchema>({
		resolver: yupResolver(summarySchema),
		mode: 'onChange',
		reValidateMode: 'onChange',
	});

	const tabProps: ITabheaderObject<tabTitle>[] = [
		{
			id: 1,
			title: 'Basic Information',
			description: 'Tell us more about yourself',
			isCompleted: personalDetailsHookForm.formState.isValid,
		},
		{
			id: 2,
			title: 'Location Information',
			description: 'Select polling unit',
			isCompleted: locationDetailsHookForm.formState.isValid,
		},
		{
			id: 3,
			title: 'Field Readiness',
			description: 'Details about your readiness to work',
			isCompleted: fieldReadinessHookForm.formState.isValid,
		},
		{
			id: 4,
			title: 'Summary',
			description: 'Make sure all entries are correct',
			isCompleted: false,
		},
	];

	const [tabSection, setTabSection] = useState<ITabheaderObject<tabTitle>>(tabProps[0]);
	// Track the highest step the user has reached so they can navigate back-and-forth
	// among already-visited steps without having to re-click Continue each time.
	const [maxReachedStep, setMaxReachedStep] = useState<number>(1);

	const constituency = states?.location?.getConstituencyById?.data;
	const electiveCategory = constituency?.electiveCategory;
	const constituencyState = constituency?.state;
	const currentElectionType = electiveCategory?.name as electionType;

	const electionEyebrow = useMemo(
		() => formatElectionEyebrow(electionData?.name, electionData?.date),
		[electionData?.name, electionData?.date]
	);

	const buildAndSubmit = () => {
		const pdData = personalDetailsHookForm.watch();
		const ldData = locationDetailsHookForm.watch();
		const frData = fieldReadinessHookForm.watch();

		const locationCodeDelimiter = _codeSeparator(ldData.entityCode ?? '');

		const getEntityCode = () => {
			const obj: Record<string, string> = {
				EC8A:
					locationCodeDelimiter.state +
						'-' +
						locationCodeDelimiter.lga +
						'-' +
						locationCodeDelimiter.ward +
						'-' +
						locationCodeDelimiter.pollingunit || '',
				EC8B: locationCodeDelimiter.state + '-' + locationCodeDelimiter.lga + '-' + locationCodeDelimiter.ward || '',
				EC8C: locationCodeDelimiter.state + '-' + locationCodeDelimiter.lga || '',
				EC8D: locationCodeDelimiter.state || '',
			};

			return obj[frData.resultType];
		};

		const data: Record<string, unknown> = {
			firstName: pdData.firstName,
			lastName: pdData.lastName,
			email: pdData.email,
			phoneNumber: pdData.phoneNumber,
			// Observers don't submit a voter's card / VIN — sent empty for that lane.
			votersCardRegistrationNumber: isObserver ? '' : pdData.votersCardRegistrationNumber,
			gender: pdData.gender,
			dateOfBirth: pdData.dateOfBirth,
			entityCode: getEntityCode(),
			resultType: frData.resultType,
			electionId: electionData?.id,
			// The lane is identified by which org id is non-zero: agents send a
			// partyId, observers send an electionObserverId; the unused one is 0
			// (matches the Volunteer create contract).
			partyId: isObserver ? 0 : partyData?.id,
			electionObserverId: isObserver ? (partnerData?.id ?? 0) : 0,
			wardOfResidence: ldData.wardOfResidence ?? null,
			profilePhoto: pdData.profileUrl ?? '',
			faceId: pdData.faceId ?? null,
			votersCardImage: isObserver ? null : (pdData.votersCardImage ?? null),
			hasSmartPhoneWithInternet: !!frData.hasSmartPhoneWithInternet,
			isAvailableAllDay: !!frData.isAvailableAllDay,
			hasPartyAgentExperience: !!frData.hasPartyAgentExperience,
			isNativeSpeaker: !!frData.isNativeSpeaker,
			hasPowerBank: !!frData.hasPowerBank,
			latitude: ldData.latitude,
			longitude: ldData.longitude,
			// Bank / payout details
			accountName: pdData.accountName ?? '',
			accountNumber: pdData.accountNumber ?? '',
			bankName: pdData.bankName ?? '',
			// Social handles
			twitterHandle: pdData.twitterHandle ?? '',
			instagramHandle: pdData.instagramHandle ?? '',
			facebookHandle: pdData.facebookHandle ?? '',
			// KYC review flags — backend may add an admin queue keyed off these.
			kycReviewRequired: !!pdData.kycReviewRequired,
			kycReviewReasons: pdData.kycReviewReasons ?? [],
			// Data-protection assent (GDPR/NDPR) — links the volunteer to the
			// audit record logged at consent time. Null if the audit endpoint
			// wasn't reachable; the policy version is always attached.
			consentReference,
			dataConsentPolicyVersion: DATA_CONSENT_POLICY_VERSION,
			// Trusted link only: adds `isTrustedAgent: true`. On the open link the
			// key is absent (not false) — see trustedInvite.ts.
			...trustedPayloadFields(Boolean(isTrustedInvite)),
		};

		if (isObserver) {
			createObserver(data as unknown as ICreateObserverApplicationRequest)
				.unwrap()
				.then((res) => {
					const created: any = res?.data;
					if (created?.id) {
						// Real success: hand the persisted entity + form context to the
						// dedicated success page (via router state + a sessionStorage
						// fallback that survives a refresh), then clear the create slice so
						// a back-navigation doesn't re-show the old inline status panel.
						const payload = buildSuccessPayload(created, {
							personal: {
								firstName: pdData.firstName,
								lastName: pdData.lastName,
								email: pdData.email,
								phoneNumber: pdData.phoneNumber,
							},
							election: { id: electionData?.id, name: electionData?.name, date: electionData?.date },
							party: isObserver
								? { shortName: partnerData?.shortName, longName: partnerData?.longName }
								: { shortName: partyData?.shortName, longName: partyData?.longName },
							location: { entityName: ldData.entityName, entityCode: ldData.entityCode },
							role: 'Election Observer',
						});
						storeSuccessPayload(payload);
						clearAction(agentType.create);
						navigate(pageurl.APPLICATIONSUCCESS, { state: payload });
						return;
					}
				})
				.catch((err) => {
					showToast('error', 'An error occurred while submitting your application. Please try again later.');
				});
		} else {
			createAgentAction(
				data,
				(res) => {
					const created = res?.data;
					if (created?.id) {
						// Real success: hand the persisted entity + form context to the
						// dedicated success page (via router state + a sessionStorage
						// fallback that survives a refresh), then clear the create slice so
						// a back-navigation doesn't re-show the old inline status panel.
						const payload = buildSuccessPayload(created, {
							personal: {
								firstName: pdData.firstName,
								lastName: pdData.lastName,
								email: pdData.email,
								phoneNumber: pdData.phoneNumber,
							},
							election: { id: electionData?.id, name: electionData?.name, date: electionData?.date },
							party: isObserver
								? { shortName: partnerData?.shortName, longName: partnerData?.longName }
								: { shortName: partyData?.shortName, longName: partyData?.longName },
							location: { entityName: ldData.entityName, entityCode: ldData.entityCode },
							role: frData.resultType,
						});
						storeSuccessPayload(payload);
						clearAction(agentType.create);
						navigate(pageurl.APPLICATIONSUCCESS, { state: payload });
						return;
					}
					if (res?.isSuccessful) {
						showToast('success', 'Application Successful');
					} else {
						showToast('error', res?.message || 'Something went wrong');
					}
				},
				() => {
					showToast('error', 'Something went wrong');
				}
			);
		}
	};

	const submitAgentForm = () => {
		if (submitConfirmedRef.current) {
			submitConfirmedRef.current = false;
			buildAndSubmit();
			return;
		}

		const pdData = personalDetailsHookForm.watch();
		if (pdData.kycReviewRequired) {
			setSoftBlockReasons((pdData.kycReviewReasons ?? []) as KYCReviewReason[]);
			setSoftBlockOpen(true);
			return;
		}

		buildAndSubmit();
	};

	const handleSoftBlockConfirm = () => {
		submitConfirmedRef.current = true;
		buildAndSubmit();
	};

	const resetData = () => {
		setTabSection(tabProps[0]);
		if (dataAgent?.isSuccessful) {
			clearAction(locationType.lgasInState);
			clearAction(locationType.wardsInLGA);
			clearAction(locationType.pollingUnitsInWard);
			clearAction(agentType.create);
		}
	};

	const onNext = (titleArg: tabTitle) => () => {
		const tab = tabProps.find((i) => i.title === titleArg);
		if (tab) {
			setTabSection(tab);
			setMaxReachedStep((prev) => Math.max(prev, tab.id));
		}
	};

	const onStepClick = (stepId: number) => {
		const tab = tabProps.find((t) => t.id === stepId);
		if (!tab) return;
		// Allow navigating to any step the user has already reached.
		if (stepId <= maxReachedStep) setTabSection(tab);
	};

	const onExit = () => {
		navigate('/');
	};

	const onPrint = () => {
		reactToPrintFn();
	};

	const onStatusAction = () => {
		if (dataAgent?.isSuccessful) {
			onPrint();
		} else {
			resetData();
		}
	};

	const isCalled = useRef<boolean>(false);

	useEffect(() => {
		if (isCalled.current) {
			return;
		}

		if (electionData?.constituencyId) {
			isCalled.current = true;
			actions?.getConstituencyByIdAction(electionData?.constituencyId + '');
		}
	}, [electionData?.constituencyId]);

	useEffect(() => {
		const existingVerification = checkExistingVerification();
		if (existingVerification) {
			setIsPhoneVerified(true);
			setVerifiedPhone(existingVerification.phoneNumber);
			personalDetailsHookForm.setValue('phoneNumber', existingVerification.phoneNumber);
		}

		const existingConsent = checkExistingConsent();
		if (existingConsent) {
			setIsDataConsentConfirmed(true);
			setConsentReference(existingConsent.consentReference);
		}
	}, []);

	const handleVerificationComplete = useCallback(
		(phone: string) => {
			setIsPhoneVerified(true);
			setVerifiedPhone(phone);
			personalDetailsHookForm.setValue('phoneNumber', phone);
		},
		[personalDetailsHookForm]
	);

	const headerCopy = STEP_TITLES[tabSection.title];
	const isFirstStep = tabSection.title === 'Basic Information';
	const partyDisplayName = partyData?.shortName || partyData?.longName;

	return (
		<>
			<PhoneVerificationModal
				isOpen={!isPhoneVerified}
				onVerificationComplete={handleVerificationComplete}
			/>

			<DataConsentModal
				isOpen={isPhoneVerified && !isDataConsentConfirmed}
				verifiedPhone={verifiedPhone}
				electionId={electionData?.id}
				partyId={partyData?.id}
				actions={actions}
				states={states}
				onConsent={(ref) => {
					setConsentReference(ref);
					setIsDataConsentConfirmed(true);
				}}
			/>

			<ReadinessChecklist
				isOpen={isPhoneVerified && isDataConsentConfirmed && !isReadinessConfirmed}
				onComplete={() => setIsReadinessConfirmed(true)}
			/>

			<SoftBlockModal
				isOpen={softBlockOpen}
				setIsOpen={setSoftBlockOpen}
				reasons={softBlockReasons}
				onConfirm={handleSoftBlockConfirm}
				onCancel={() => {
					submitConfirmedRef.current = false;
				}}
			/>

			<BCColumn>
				{dataAgent?.isSuccessful ? (
					<div>
						<StatusComponent
							status={dataAgent?.isSuccessful || false}
							onExit={onExit}
							onStatusAction={onStatusAction}
						/>
						<PrintSummary
							personalDetailsHookForm={personalDetailsHookForm}
							locationDetailsHookForm={locationDetailsHookForm}
							fieldReadinessDetailsHookForm={fieldReadinessHookForm}
							contentRef={contentRef}
						/>
					</div>
				) : (
					<div
						style={{
							filter: !isPhoneVerified || !isDataConsentConfirmed || !isReadinessConfirmed ? 'blur(2px)' : 'none',
							pointerEvents: !isPhoneVerified || !isDataConsentConfirmed || !isReadinessConfirmed ? 'none' : 'auto',
							transition: 'filter 0.3s ease',
						}}
					>
						<CreateAgentForm onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
							<ProfileUserSection>
								{isFirstStep && customization ? (
									<BrandedHero
										customization={customization}
										election={electionData}
										trustedInvite={isTrustedInvite}
										partyName={partyDisplayName}
									/>
								) : (
									<>
										{isFirstStep && isTrustedInvite && (
											<TrustedInviteNotice
												variant='on-page'
												partyName={partyDisplayName}
											/>
										)}
										<PageHeaderBlock>
											{electionEyebrow && <PageEyebrow>{electionEyebrow}</PageEyebrow>}
											<PageTitle>{headerCopy?.title ?? tabSection.title}</PageTitle>
											<PageSubtitle>{headerCopy?.subtitle ?? ''}</PageSubtitle>
										</PageHeaderBlock>
									</>
								)}

								{tabSection.title === 'Basic Information' && customization?.showVideo && customization?.videoUrl && (
									<VideoThumbnailCard customization={customization} />
								)}

								<StepIndicator
									steps={tabProps.map((t) => ({
										id: t.id,
										title: t.title,
										isCompleted: t.isCompleted,
									}))}
									currentStepId={tabSection.id}
									maxReachedStepId={maxReachedStep}
									onStepClick={onStepClick}
								/>

								<div className={tabSection.title === 'Basic Information' ? '' : 'd-none'}>
									<BasicInfo
										hookForm={personalDetailsHookForm}
										onNext={personalDetailsHookForm.handleSubmit(onNext('Location Information'))}
										actions={actions}
										states={states}
										electionId={electionData?.id}
										role={role}
									/>
								</div>

								<div className={tabSection.title === 'Location Information' ? '' : 'd-none'}>
									<LocationInfo
										dataStates={dataStates}
										loadStates={loadStates || false}
										actions={actions}
										datalga={datalga}
										dataWard={dataWard}
										dataPollingUnit={dataPollingUnit}
										loadLga={loadLga}
										loadWard={loadWard}
										loadPollingUnit={loadPollingUnit}
										onPrevious={onNext('Basic Information')}
										onNext={locationDetailsHookForm.handleSubmit(onNext('Field Readiness'))}
										hookForm={locationDetailsHookForm}
										setSelectedState={setSelectedState}
										selectedState={selectedState}
										currentElectionType={currentElectionType}
										constituencyState={constituencyState}
									/>
								</div>

								<div className={tabSection.title === 'Field Readiness' ? '' : 'd-none'}>
									<FieldReadiness
										onPrevious={onNext('Location Information')}
										onNext={fieldReadinessHookForm.handleSubmit(onNext('Summary'))}
										hookForm={fieldReadinessHookForm}
										role={role}
									/>
								</div>

								<div className={tabSection.title === 'Summary' ? '' : 'd-none'}>
									<FormSummary
										prevPage={onNext('Field Readiness')}
										onSubmit={submitAgentForm}
										onEdit={onNext}
										personalDetailsHookForm={personalDetailsHookForm}
										locationDetailsHookForm={locationDetailsHookForm}
										fieldReadinessDetailsHookForm={fieldReadinessHookForm}
										summaryHookForm={summaryHookForm}
										isLoading={!!loadAgent}
										contentRef={contentRef}
									/>
								</div>
							</ProfileUserSection>
						</CreateAgentForm>
					</div>
				)}
			</BCColumn>
		</>
	);
};

export default React.memo(CreateAgent);
